import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    financeApi,
    PaginatedResponse,
    PaymentListItem,
    PaymentDetail,
    PaymentCandidatesResponse,
    CreatePaymentPayload,
} from '../../services/financeApi';

interface FinancePaymentsState {
    list: PaymentListItem[];
    pagination: PaginatedResponse<PaymentListItem>['meta']['pagination'] | null;
    customers: PaginatedResponse<PaymentListItem>['meta']['customers'];
    loadingList: boolean;
    loadingDetail: boolean;
    loadingCandidates: boolean;
    creatingPayment: boolean;
    error: string | null;
    selectedPayment: PaymentDetail | null;
    candidates: PaymentCandidatesResponse['data'] | null;
}

const initialState: FinancePaymentsState = {
    list: [],
    pagination: null,
    customers: [],
    loadingList: false,
    loadingDetail: false,
    loadingCandidates: false,
    creatingPayment: false,
    error: null,
    selectedPayment: null,
    candidates: null,
};

export const fetchFinancePayments = createAsyncThunk(
    'financePayments/fetchList',
    async (params: { customerId?: number; page?: number; perPage?: number } = {}) => {
        return await financeApi.getPayments(params);
    }
);

export const fetchFinancePayment = createAsyncThunk(
    'financePayments/fetchDetail',
    async (paymentId: number) => {
        const response = await financeApi.getPayment(paymentId);
        return response.data;
    }
);

export const fetchFinancePaymentCandidates = createAsyncThunk(
    'financePayments/fetchCandidates',
    async (params: { customerId?: number; fromDate?: string; toDate?: string } = {}) => {
        const response = await financeApi.getPaymentCandidates(params);
        return response.data;
    }
);

export const createFinancePayment = createAsyncThunk(
    'financePayments/create',
    async (payload: CreatePaymentPayload) => {
        const response = await financeApi.createPayment(payload);
        return response.data;
    }
);

const financePaymentsSlice = createSlice({
    name: 'financePayments',
    initialState,
    reducers: {
        clearFinancePaymentError: (state) => {
            state.error = null;
        },
        clearSelectedFinancePayment: (state) => {
            state.selectedPayment = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchFinancePayments.pending, (state) => {
                state.loadingList = true;
                state.error = null;
            })
            .addCase(fetchFinancePayments.fulfilled, (state, action: PayloadAction<PaginatedResponse<PaymentListItem>>) => {
                state.loadingList = false;
                state.list = action.payload.data;
                state.pagination = action.payload.meta.pagination;
                state.customers = action.payload.meta.customers;
            })
            .addCase(fetchFinancePayments.rejected, (state, action) => {
                state.loadingList = false;
                state.error = action.error.message || 'Failed to load payments';
            });

        builder
            .addCase(fetchFinancePayment.pending, (state) => {
                state.loadingDetail = true;
                state.error = null;
            })
            .addCase(fetchFinancePayment.fulfilled, (state, action: PayloadAction<PaymentDetail>) => {
                state.loadingDetail = false;
                state.selectedPayment = action.payload;
            })
            .addCase(fetchFinancePayment.rejected, (state, action) => {
                state.loadingDetail = false;
                state.error = action.error.message || 'Failed to load payment detail';
            });

        builder
            .addCase(fetchFinancePaymentCandidates.pending, (state) => {
                state.loadingCandidates = true;
                state.error = null;
            })
            .addCase(fetchFinancePaymentCandidates.fulfilled, (state, action: PayloadAction<PaymentCandidatesResponse['data']>) => {
                state.loadingCandidates = false;
                state.candidates = action.payload;
            })
            .addCase(fetchFinancePaymentCandidates.rejected, (state, action) => {
                state.loadingCandidates = false;
                state.error = action.error.message || 'Failed to load payment candidates';
            });

        builder
            .addCase(createFinancePayment.pending, (state) => {
                state.creatingPayment = true;
                state.error = null;
            })
            .addCase(createFinancePayment.fulfilled, (state, action: PayloadAction<PaymentDetail>) => {
                state.creatingPayment = false;
                state.selectedPayment = action.payload;
            })
            .addCase(createFinancePayment.rejected, (state, action) => {
                state.creatingPayment = false;
                state.error = action.error.message || 'Failed to create payment';
            });
    },
});

export const { clearFinancePaymentError, clearSelectedFinancePayment } = financePaymentsSlice.actions;
export default financePaymentsSlice.reducer;

